import React from 'react';
import { motion } from 'framer-motion';
import { MeterBar } from './MeterBar';
import { channels, exploredAllThree, firstConsideration, nextStepChosen, sceneFunnel, segments } from '../../data/snakInsights';
const EASE = [0.23, 1, 0.32, 1] as const;
function Card({
  title,
  sub,
  children,
  delay = 0,
  className = ''
}: {
  title: string;
  sub?: string;
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  return <motion.section initial={{
    opacity: 0,
    y: 12
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay,
    duration: 0.45,
    ease: EASE
  }} className={`flex flex-col rounded-2xl bg-white p-[clamp(1rem,1.6vw,1.4rem)] ring-1 ring-[var(--fg)]/[0.07] ${className}`} data-id="element-105" data-mp="1am2gis7t5lht">
      <h3 className="text-[clamp(0.85rem,1.1vw,1rem)] font-bold text-[var(--fg)]" data-id="element-106" data-mp="1ollckmnch6dr">{title}</h3>
      {sub && <p className="mt-0.5 text-[clamp(0.68rem,0.82vw,0.78rem)] text-[var(--muted)]" data-id="element-107" data-mp="2uzkpa0l79vrj">{sub}</p>}
      <div className="mt-4 flex-1" data-id="element-108" data-mp="2zyntjitcvxyg">{children}</div>
    </motion.section>;
}
export function QuantitativePanel() {
  const maxReached = sceneFunnel[0].reached;
  const maxDwell = Math.max(...sceneFunnel.map(s => s.dwell));
  return <div className="grid gap-[clamp(0.6rem,1vw,0.9rem)] lg:grid-cols-12" data-id="element-109" data-mp="28el70zxxqgcd">
      <Card title="Scene-by-scene journey" sub="HCPs reaching each scene · average dwell time" delay={0.05} className="lg:col-span-7" data-id="element-110" data-mp="23df82kdolwue">
        <ol className="space-y-3" data-id="element-111" data-mp="1yhzxq2evuj2q">
          {sceneFunnel.map((s, i) => <li key={s.scene} className="grid grid-cols-[minmax(0,7.5rem)_1fr_3.5rem] items-center gap-3" data-id="element-112" data-mp="1uflhzr6ifzfo" data-mpi={i}>
              <span className="truncate text-[clamp(0.72rem,0.9vw,0.85rem)] font-medium text-[var(--fg)]" data-id="element-113" data-mp="1pm0n4807rlkp">
                <span className="mr-1.5 font-mono text-[var(--muted)]" data-id="element-114" data-mp="1km4nekyjugay">0{i + 1}</span>
                {s.scene}
              </span>
              <div className="relative h-6 overflow-hidden rounded-md bg-[var(--fg)]/[0.05]" data-id="element-115" data-mp="1aqkrhyjzjhm7">
                <motion.div className="h-full rounded-md bg-[var(--brand)]" initial={{
              width: 0
            }} animate={{
              width: `${s.reached / maxReached * 100}%`
            }} transition={{
              delay: 0.15 + i * 0.08,
              duration: 0.7,
              ease: EASE
            }} data-id="element-116" data-mp="16opu3556dodp" />
                <span className="absolute inset-y-0 left-2.5 flex items-center text-[0.72rem] font-bold tabular-nums text-white" data-id="element-117" data-mp="n3fseyc42hg8">{s.reached}</span>
              </div>
              <span className="flex items-center justify-end gap-1.5 text-[0.72rem] tabular-nums text-[var(--muted)]" data-id="element-118" data-mp="s3lm0k1xye0y">
                <span className="h-1.5 rounded-full bg-[var(--accent)]" style={{
              width: `${s.dwell / maxDwell * 22}px`
            }} aria-hidden="true" data-id="element-119" data-mp="1jsor221njsen" />
                {s.dwell}s
              </span>
            </li>)}
        </ol>
        <p className="mt-4 border-t border-[var(--fg)]/[0.07] pt-3 text-[clamp(0.7rem,0.85vw,0.8rem)] text-[var(--muted)]" data-id="element-120" data-mp="1azbrwiczvuk4">
          Biggest drop: <span className="font-semibold text-[var(--fg)]" data-id="element-121" data-mp="1yocuhoz5cbhh">Interact → Reveal (−19%)</span> · Longest dwell:{' '}
          <span className="font-semibold text-[var(--fg)]" data-id="element-122" data-mp="2cvpuidhtdwrc">PFS evidence (42s)</span>
        </p>
      </Card>

      <Card title="What they looked at first" sub="First clinical consideration opened" delay={0.12} className="lg:col-span-5" data-id="element-123" data-mp="28ue1rz3n8vex">
        <div className="space-y-3.5" data-id="element-124" data-mp="2mkhkwh788905">
          {firstConsideration.map((c, i) => <MeterBar key={c.label} label={c.label} value={c.share} delay={0.25 + i * 0.08} strong={i === 0} data-id="element-125" data-mp="2hm68pztglmer" data-mpi={i} />)}
        </div>
        <div className="mt-5 flex items-center gap-4 rounded-xl bg-[var(--surface)] p-3" data-id="element-126" data-mp="1txdimjziq8s2">
          <p className="text-[clamp(1.6rem,2.6vw,2.4rem)] font-extrabold leading-none tracking-[-0.04em] text-[var(--brand)]" data-id="element-127" data-mp="1pwurks5g96qx">{exploredAllThree}%</p>
          <p className="text-[clamp(0.7rem,0.85vw,0.8rem)] leading-snug text-[var(--muted)]" data-id="element-128" data-mp="1yw827o5b81d8">explored all three considerations before moving to the evidence</p>
        </div>
      </Card>

      <Card title="Segment comparison" sub="Behaviour differs by specialty" delay={0.18} className="lg:col-span-5" data-id="element-129" data-mp="3fc15a4fk7t3q">
        <div className="grid grid-cols-2 gap-4" data-id="element-130" data-mp="1ju9mp2wf2xkf">
          {segments.map(seg => <div key={seg.name} data-id="element-131" data-mp="1ot32j1z6f0nr">
              <p className="text-[clamp(0.78rem,0.95vw,0.9rem)] font-bold text-[var(--fg)]" data-id="element-132" data-mp="1osb7ptdel6yj">{seg.name}</p>
              <p className="font-mono text-[0.62rem] text-[var(--muted)]" data-id="element-133" data-mp="1tr4gx9lnqbsw">n={seg.n}</p>
              <div className="mt-3 space-y-3" data-id="element-134" data-mp="1xu15ri1sj45a">
                {seg.metrics.map((m, i) => <MeterBar key={m.label} label={m.label} value={m.value} delay={0.3 + i * 0.08} data-id="element-135" data-mp="22tnkjvx9osg7" data-mpi={i} />)}
              </div>
            </div>)}
        </div>
      </Card>

      <Card title="Where they went next" sub="Choice on the final scene" delay={0.24} className="lg:col-span-3" data-id="element-136" data-mp="2ci5gnzulqbdi">
        <div className="space-y-3" data-id="element-137" data-mp="2hi1lu2f1c8un">
          {nextStepChosen.map((n, i) => <MeterBar key={n.label} label={n.label} value={n.share} delay={0.35 + i * 0.08} strong={i === 0} data-id="element-138" data-mp="2ljn1twmv9392" data-mpi={i} />)}
        </div>
      </Card>

      <Card title="By channel" sub="Share of starts · completion" delay={0.3} className="lg:col-span-4" data-id="element-139" data-mp="2qiztqvslh0ii">
        <table className="w-full text-[clamp(0.72rem,0.9vw,0.85rem)]" data-id="element-140" data-mp="eweed0l7pk84">
          <thead data-id="element-141" data-mp="9uyv9e2lhnsd">
            <tr className="text-left font-mono text-[0.62rem] uppercase tracking-[0.12em] text-[var(--muted)]" data-id="element-142" data-mp="9ugq1rxyfz8b">
              <th className="pb-2 font-medium" data-id="element-143" data-mp="5wixcf4z6v06">Channel</th>
              <th className="pb-2 text-right font-medium" data-id="element-144" data-mp="wde9ncrugtp">Starts</th>
              <th className="pb-2 text-right font-medium" data-id="element-145" data-mp="xlcnp5p3donh">Completed</th>
            </tr>
          </thead>
          <tbody data-id="element-146" data-mp="tk93k700h6gg">
            {channels.map(c => <tr key={c.label} className="border-t border-[var(--fg)]/[0.07]" data-id="element-147" data-mp="tksntud4w6xn">
                <td className="py-2 font-medium text-[var(--fg)]" data-id="element-148" data-mp="orzib49ucuki">{c.label}</td>
                <td className="py-2 text-right tabular-nums text-[var(--muted)]" data-id="element-149" data-mp="juzbgr563nsc">{c.starts}%</td>
                <td className="py-2 text-right" data-id="element-150" data-mp="1h9tp8xnjuhtf">
                  <span className={`inline-block rounded-full px-2 py-0.5 font-bold tabular-nums ${c.completion >= 70 ? 'bg-[var(--brand)] text-white' : 'bg-[var(--surface)] text-[var(--fg)]'}`} data-id="element-151" data-mp="1hboakd2qgby1">
                    {c.completion}%
                  </span>
                </td>
              </tr>)}
          </tbody>
        </table>
      </Card>
    </div>;
}